// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@conklin/App/Components/Common/XngZone";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@conklin/Overrides/Components/CartTotalDisplay";
import StyledBox from "@conklin/XDL/Components/Common/Box";
import { Divider } from "@conklin/XDL/Utilities";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canApplyPromotionsToCart,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotions = getCurrentPromotionsDataView(state).value;
    return {
        cart: cartState.value,
        orderPromotions: promotions ? getOrderPromotions(promotions) : undefined,
        shippingPromotions: promotions ? getShippingPromotions(promotions) : undefined,
        discountTotal: promotions ? getDiscountTotal(promotions) : undefined,
        isLoading: !cartState.value || cartState.isLoading,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        // XNG-Changes
        canApplyPromosToCart: canApplyPromotionsToCart(cartState.value),
        showTaxAndShipping: getSettingsCollection(state)?.cartSettings?.showTaxAndShipping,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutOrderSummaryStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    editGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    // XNG-Change
    messageText?: TypographyPresentationProps;
}

export const orderSummaryStyles: OnePageCheckoutOrderSummaryStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    editGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    cartTotalGridItem: { width: 12 },
    cartTotal: {
        container: {
            gap: 10,
            css: css`
                padding: 0;
                background-color: transparent;
            `,
        },
    },
};

const styles = orderSummaryStyles;

const OnePageCheckoutOrderSummary = ({
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    cartUrl,
    // XNG-Changes
    id,
    canApplyPromosToCart,
    showTaxAndShipping,
}: Props) => {
    // if (isLoading) {
    //     return (
    //         <StyledWrapper {...styles.centeringWrapper}>
    //             <LoadingSpinner data-test-selector="onePageCheckout_orderSummary" />
    //         </StyledWrapper>
    //     );
    // }
    const locationBasedMsg = siteMessage("Custom_OrderSummary_LocationBasedPricing").toString();

    // XNG-Changes: structure
    return (
        // <GridContainer {...styles.container}>
        //     <GridItem {...styles.titleGridItem}>
        //         <Typography as="h2" {...styles.titleText}>
        //             {translate("Order Summary")}
        //         </Typography>
        //     </GridItem>
        //     <GridItem {...styles.editGridItem}>
        //         <Link {...styles.editLink} href={cartUrl}>
        //             {translate("Edit")}
        //         </Link>
        //     </GridItem>
        //    <GridItem {...styles.cartTotalGridItem}>
        <StyledBox>
            <CartTotalDisplay
                cart={cart}
                orderPromotions={orderPromotions}
                shippingPromotions={shippingPromotions}
                discountTotal={discountTotal}
                // XNG-Changes
                isLoading={isLoading}
                showTaxAndShipping={showTaxAndShipping}
            />

            <Divider margin="12px 0 " />
            <Typography {...styles.messageText}>{siteMessage("Custom_OrderSummary_ShippingAndHandling")}</Typography>

            <Typography {...styles.messageText}>{siteMessage("Custom_OrderSummary_MicroCopy")}</Typography>

            {locationBasedMsg !== "Custom_OrderSummary_LocationBasedPricing" && (
                <Typography {...styles.messageText}>{locationBasedMsg}</Typography>
            )}
            <XngZone contentId={id} zoneName="actions" />
        </StyledBox>
        //    </GridItem>
        // </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutOrderSummary),
    definition: {
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
        displayName: "Order Summary",
    },
};

export default widgetModule;
